import * as React from 'react';
import { Fabric, PrimaryButton, TextField } from '@fluentui/react';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { viewStyles } from 'styles/viewStyles';
import { formStyles } from 'styles/formStyles';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { Ansprechpartner, useAnsprechpartnerList } from 'api/ansprechpartner';
import { AnsprechpartnerEdit } from 'components/Kunde/Ansprechpartner/AnsprechpartnerEdit';
import { AnsprechpartnerItem } from 'components/Kunde/Ansprechpartner/AnsprechpartnerItem';
import { useParams } from 'react-router-dom';
import { RecordViewUrlParams } from 'layout/App/types';
import { Helmet } from 'react-helmet';
import { Checkbox } from '@fluentui/react/lib/Checkbox';

const styles = mergeStyleSets({
    searchBar: {
        display: 'grid',
        gridTemplateColumns: '200px max-content max-content 1fr 200px',
        gridColumnGap: 10,
        marginTop: 10,
    },
});

interface AnsprechpartnerViewProps {
    kNr?: number;
}

interface AnsprechpartnerEditData {
    kNr: number;
    kaNr: number;
}

export const AnsprechpartnerView: React.FC<AnsprechpartnerViewProps> = ({ kNr }) => {
    const [ansprechpartnerEditData, setAnsprechpartnerEditData] = React.useState<AnsprechpartnerEditData | null>(null);
    const { urlKundeNr } = useParams<RecordViewUrlParams>();

    const [searchText, setSearchText] = React.useState('');
    const [sortByName, setSortByName] = React.useState(false);
    const [showArchiv, setShowArchiv] = React.useState(false);

    const ansprechpartnerList = useAnsprechpartnerList({
        kNr,
        includeArchiv: showArchiv,
        withKunde: false,
        maxRows: 250,
    });

    const filteredAnsprechpartnerList = React.useMemo<Ansprechpartner[]>(() => {
        return (ansprechpartnerList.data || [])
            .filter((ap) => searchText === '' || ap.name.toLocaleLowerCase().includes(searchText.toLowerCase()))
            .sort((a, b) => (sortByName ? a.name.localeCompare(b.name) : b.nr - a.nr));
    }, [ansprechpartnerList.data, searchText, sortByName]);

    return (
        <Fabric className={viewStyles.subItemContainer}>
            <Helmet>
                <title>
                    {urlKundeNr ? 'Kunde ' + urlKundeNr + ' | ' : ''}
                    Ansprechpartner
                </title>
            </Helmet>
            {ansprechpartnerList.isLoading || ansprechpartnerList.isFetching ? (
                <LoadingIndicator />
            ) : (
                <>
                    <div className={styles.searchBar}>
                        <TextField
                            value={searchText}
                            onChange={(event, newValue): void => {
                                setSearchText(newValue === undefined ? '' : newValue);
                            }}
                            placeholder="Suche"
                        />

                        <div>
                            <Checkbox
                                className={formStyles.checkboxInsideForm}
                                label="Sortieren nach Name"
                                checked={sortByName}
                                onChange={(event, checked) => {
                                    setSortByName(checked || false);
                                }}
                            />
                        </div>

                        <div>
                            <Checkbox
                                className={formStyles.checkboxInsideForm}
                                label="Archivierte Ansprechpartner anzeigen"
                                checked={showArchiv}
                                onChange={(event, checked) => {
                                    setShowArchiv(checked || false);
                                }}
                            />
                        </div>

                        <div />

                        {kNr && (
                            <PrimaryButton
                                onClick={() =>
                                    setAnsprechpartnerEditData({
                                        kNr,
                                        kaNr: -1,
                                    })
                                }
                                text="Neuer Ansprechpartner"
                            />
                        )}
                    </div>
                    <div className={viewStyles.listContainer}>
                        {filteredAnsprechpartnerList.length === 0 ? (
                            <div className={formStyles.noData}>Keine Ansprechpartner.</div>
                        ) : (
                            filteredAnsprechpartnerList.map((ansprechpartner) => (
                                <AnsprechpartnerItem
                                    key={ansprechpartner.nr}
                                    ansprechpartner={ansprechpartner}
                                    editAnsprechpartner={() =>
                                        setAnsprechpartnerEditData({
                                            kNr: ansprechpartner.kNr,
                                            kaNr: ansprechpartner.nr || 0,
                                        })
                                    }
                                />
                            ))
                        )}
                    </div>
                </>
            )}
            {ansprechpartnerEditData !== null && (
                <AnsprechpartnerEdit
                    kNr={ansprechpartnerEditData.kNr}
                    kaNr={ansprechpartnerEditData.kaNr}
                    onClose={(refetch) => {
                        setAnsprechpartnerEditData(null);
                        if (refetch) {
                            ansprechpartnerList.refetch().then();
                        }
                    }}
                />
            )}
        </Fabric>
    );
};
